import React, { useEffect, useRef, useState } from 'react';

import { computeEstimate, formatRupees } from './estimateModel';
import { estimatePdfUrl } from '../../services/api/admissionApi';
import { listBedCategories } from '../../services/api/bedCategoryApi';
import InfoHint from '../common/InfoHint';

/**
 * EstimatePanel (S6) — the "Estimate — not a bill" cost sheet. PURE client-side
 * recompute via the shared `computeEstimate` selector (single source of truth):
 * expected days × bed tariff (+ nursing/RMO) + planned-intervention lines + free
 * text lines. ZERO network on any change — the server re-snapshots
 * authoritatively at submit / plan-estimate save. Editable with no bed
 * (emergency) — bed-day lines simply drop out.
 *
 * Controlled by the parent:
 *   value = { expectedStayDays, bedCategory, plannedInterventions, otherLines }
 *   onChangeDays(days) · onChangeOtherLines(lines) · onRemoveIntervention(index)
 *   admissionId (optional) → enables the print link (§2.8).
 */
export default function EstimatePanel({
  value,
  onChangeDays,
  onChangeOtherLines,
  onRemoveIntervention,
  admissionId,
  assignLater = false,          // S10: no bed + "assign later" → planned-category mode
  expectedBedCategoryId = '',
  onChangeExpectedBedCategory,
  moneyHidden = false,          // admission-access: `isMoneyHidden(admission)` — expected days only, no amounts
}) {
  const v = value || {};

  // Planned-category picker (S10): only when there is no real bed AND assign-later
  // is on. Categories fetched ONCE (bounded, single-flight via loaded ref) the
  // first time the picker is needed — never per render, and not at all otherwise.
  const noBedMode = !moneyHidden && assignLater && !v.bedCategory;
  const [categories, setCategories] = useState([]);
  const categoriesLoadedRef = useRef(false);
  useEffect(() => {
    if (!noBedMode || categoriesLoadedRef.current) return undefined;
    categoriesLoadedRef.current = true;
    let alive = true;
    listBedCategories().then((res) => {
      if (alive && res && res.success) setCategories(res.data.bed_categories || []);
    });
    return () => { alive = false; };
  }, [noBedMode]);

  const plannedCategory = noBedMode
    ? (categories.find((c) => c.bed_category_id === expectedBedCategoryId) || null)
    : null;

  const est = computeEstimate({
    expectedStayDays: v.expectedStayDays,
    bedCategory: noBedMode ? plannedCategory : v.bedCategory,
    plannedInterventions: v.plannedInterventions,
    otherLines: v.otherLines,
    plannedCategory: noBedMode && !!plannedCategory,
    bedTbd: noBedMode && !plannedCategory,
  });
  const interventions = Array.isArray(v.plannedInterventions) ? v.plannedInterventions : [];
  const otherLines = Array.isArray(v.otherLines) ? v.otherLines : [];

  const addOtherLine = () =>
    onChangeOtherLines([...otherLines, { label: '', amount: 0 }]);
  const setOtherLine = (i, patch) =>
    onChangeOtherLines(otherLines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeOtherLine = (i) =>
    onChangeOtherLines(otherLines.filter((_, idx) => idx !== i));

  const lineCls = 'flex justify-between py-1.5 text-[13px] border-b border-dashed border-slate-200 dark:border-slate-700';

  return (
    <div data-testid="estimate-panel">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Estimate / cost sheet</h3>
        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-amber-400 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
          Estimate — not a bill
        </span>
      </div>

      <div className="mb-3">
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" htmlFor="est-days">
          Expected stay (days)
        </label>
        <input
          id="est-days"
          type="number"
          min="0"
          className="w-24 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          value={v.expectedStayDays ?? ''}
          onChange={(e) => onChangeDays(e.target.value === '' ? null : Number(e.target.value))}
        />
      </div>

      {noBedMode && (
        <div className="mb-3" data-testid="planned-category-picker">
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" htmlFor="est-planned-cat">
            Planned bed category (optional)
          </label>
          <select
            id="est-planned-cat"
            className="w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            value={expectedBedCategoryId || ''}
            onChange={(e) => onChangeExpectedBedCategory && onChangeExpectedBedCategory(e.target.value || '')}
            aria-label="Planned bed category"
          >
            <option value="">Bed — to be assigned</option>
            {categories.map((c) => (
              <option key={c.bed_category_id} value={c.bed_category_id}>
                {c.name} · {formatRupees(c.per_day_charge)}/day
              </option>
            ))}
          </select>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
            Prices the estimated bed-days before a bed is chosen. Leave blank to show the bed cost as to be assigned.
          </p>
        </div>
      )}

      {!moneyHidden && est.lines.length === 0 && interventions.length === 0 && otherLines.length === 0 && (
        <p className="text-[12.5px] text-slate-500 dark:text-slate-400 mb-2">
          Set expected days and pick a bed — or add a planned intervention — to build the estimate.
        </p>
      )}

      {/* Bed-day + interventions summary lines from the shared selector */}
      {!moneyHidden && est.lines.map((l) => (
        <div className={lineCls} key={l.key}>
          <span className="text-slate-500 dark:text-slate-400">{l.label}</span>
          <span className="text-slate-800 dark:text-slate-100">{formatRupees(l.amount)}</span>
        </div>
      ))}

      {/* Removable planned-intervention rows (detail) */}
      {interventions.length > 0 && (
        <div className="mt-2 mb-1" data-testid="planned-rows">
          {interventions.map((line, i) => (
            <div
              key={`${line.catalog_ref}-${i}`}
              className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 mb-1.5 text-[12.5px]"
            >
              <span className="flex-1 font-medium text-slate-800 dark:text-slate-100">
                {line.name_snapshot}
                {line.billing_mode === 'PACKAGE' && (
                  <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-teal-400 bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200">
                    Package
                  </span>
                )}
              </span>
              {!moneyHidden && <span className="text-slate-800 dark:text-slate-100">{formatRupees(line.price_snapshot)}</span>}
              {onRemoveIntervention && (
                <button
                  type="button"
                  onClick={() => onRemoveIntervention(i)}
                  className="px-2 py-1 text-[11px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Free-text ad-hoc lines (label + amount — money) */}
      {!moneyHidden && otherLines.map((l, i) => (
        <div className="flex items-center gap-2 my-1.5" key={`other-${i}`}>
          <input
            className="flex-1 px-2 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]"
            placeholder="Description"
            value={l.label || ''}
            onChange={(e) => setOtherLine(i, { label: e.target.value })}
            aria-label={`Estimate line ${i + 1} label`}
          />
          <input
            type="number"
            min="0"
            className="w-24 px-2 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]"
            placeholder="Amount"
            value={l.amount ?? ''}
            onChange={(e) => setOtherLine(i, { amount: e.target.value === '' ? 0 : Number(e.target.value) })}
            aria-label={`Estimate line ${i + 1} amount`}
          />
          <button
            type="button"
            onClick={() => removeOtherLine(i)}
            className="px-2 py-1 text-[11px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
          >
            ✕
          </button>
        </div>
      ))}

      {!moneyHidden && (
        <>
          <button
            type="button"
            onClick={addOtherLine}
            className="w-full my-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px]"
          >
            + Add another line
          </button>

          <div className="flex justify-between pt-2 text-[15px] font-bold text-slate-900 dark:text-white">
            <span>Estimated total</span>
            <span data-testid="estimate-grand-total">{formatRupees(est.grandTotal)}</span>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 flex items-center gap-1">
            <span>Uses today&rsquo;s rates — the final bill may differ.</span>
            <InfoHint text="This estimate is saved with the admission for reference. It updates as you change the bed, stay length or planned interventions, and it does not add any charges to the patient's bill." />
          </p>
        </>
      )}

      {/* The estimate PDF is a money-only route (403 for a hidden viewer). */}
      {admissionId && !moneyHidden && (
        <a
          href={estimatePdfUrl(admissionId)}
          target="_blank"
          rel="noreferrer"
          className="block text-center mt-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px]"
        >
          🖨 Print estimate
        </a>
      )}
    </div>
  );
}
